import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Gift, Minus, Plus, ShoppingBag, Star, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { useCart, itemKey } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ChildPicker, { childToStudent } from "@/components/ChildPicker";
import AssociationPicker from "@/components/AssociationPicker";
import RelayPointPicker from "@/components/RelayPointPicker";
import CartAccountGate from "@/components/CartAccountGate";
import { getReferral } from "@/lib/referral";
import PaymentMethodPicker, { chequeContactOk } from "@/components/PaymentMethodPicker";
import { api, formatPrice } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, subtotal, giftWrap, setGiftWrap } = useCart();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [methods, setMethods] = useState([]);
  const [methodId, setMethodId] = useState(null);
  const [postal, setPostal] = useState("");
  const [assocEnabled, setAssocEnabled] = useState(false);
  const [assoc, setAssoc] = useState(null);
  const [relayPoint, setRelayPoint] = useState(null);
  const navigate = useNavigate();
  const [chequeSettings, setChequeSettings] = useState(null);
  const [payMethod, setPayMethod] = useState("card");
  const [chequeContact, setChequeContact] = useState({ name: "", email: "", phone: "", address_line: "", postal_code: "", city: "" });
  const [welcome, setWelcome] = useState({ percent: 0, name: null, invite: null });
  const [slots, setSlots] = useState(null);
  const [slotId, setSlotId] = useState("");
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [schoolMode, setSchoolMode] = useState(false);
  const [kidId, setKidId] = useState(null);
  const [kid, setKid] = useState({ first: "", last: "", klass: "" });

  useEffect(() => {
    setSchoolInfo(null); setSchoolMode(false); setKidId(null); setKid({ first: "", last: "", klass: "" });
  }, [user?.id]);

  useEffect(() => {
    if (!user || !isOpen) return;
    api.get("/customer/children").then(({ data }) => {
      setSchoolInfo(data.school ? data : null);
      const first = data.children.find((c) => !c.school_id || c.school_id === data.school?.school_id);
      if (first && !kid.first && kidId === null) { setKidId(first.id); setKid(childToStudent(first)); }
    }).catch(() => setSchoolInfo(null));
  }, [user, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tiers, setTiers] = useState([]);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [useLoyalty, setUseLoyalty] = useState(false);
  const [weightMap, setWeightMap] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    api.get("/shipping").then(({ data }) => {
      setMethods(data);
      setMethodId((cur) => {
        if (cur && (cur === "association" || data.some((m) => m.method_id === cur))) return cur;
        return (data.find((m) => m.free_above_cents) || data[0])?.method_id || null;
      });
    }).catch(() => {});
    api.get("/payments/cheque-settings").then(({ data }) => setChequeSettings(data)).catch(() => setChequeSettings(null));
    api.get("/associations/enabled").then(({ data }) => setAssocEnabled(data.enabled)).catch(() => setAssocEnabled(false));
    api.get("/pricing").then(({ data }) => setTiers(data)).catch(() => {});
    api.get("/loyalty/settings").then(({ data }) => setLoyaltySettings(data)).catch(() => {});
    api.get("/products").then(({ data }) => {
      setWeightMap(Object.fromEntries(data.map((p) => [p.product_id, p.shipping_weight ?? 250])));
    }).catch(() => {});
    if (user) refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const postalOk = /^[0-9]{5}$/.test(postal);
  useEffect(() => { setAssoc(null); }, [postal]);
  useEffect(() => { if (!assocEnabled && methodId === "association") setMethodId(methods[0]?.method_id || null); }, [assocEnabled, methodId, methods]);
  const isAvailable = (m) => {
    if (!m.zone || m.zone === "all") return true;
    if (!postalOk) return true;
    if (m.zone === "metro") {
      const d = parseInt(postal.slice(0, 2), 10);
      return d >= 1 && d <= 95;
    }
    if (m.zone === "postal") return (m.postal_prefixes || []).some((p) => postal.startsWith(p));
    return true;
  };

  // Quantité totale d'articles classiques (hors coffrets) pour la remise panier globale
  const cartQuantity = items.reduce((sum, i) => sum + ((i.box_selection && i.box_selection.length) ? 0 : i.quantity), 0);
  const globalDiscount = Math.max(
    0,
    ...tiers.filter((t) => t.scope === "global" && t.min_quantity <= cartQuantity).map((t) => t.discount_percent)
  );

  // Règle métier : une remise de catégorie prime toujours sur la remise produit ; la remise panier s'ajoute en « meilleure offre »
  const discountFor = (item) => {
    if (item.box_selection && item.box_selection.length) return 0;
    const applicable = tiers.filter(
      (t) =>
        t.min_quantity <= item.quantity &&
        ((t.scope === "product" && t.target_id === item.product_id) ||
          (t.scope === "category" && item.category && t.target_id === item.category))
    );
    const categoryTiers = applicable.filter((t) => t.scope === "category");
    let scoped = 0;
    if (categoryTiers.length) scoped = Math.max(...categoryTiers.map((t) => t.discount_percent));
    else if (applicable.length) scoped = Math.max(...applicable.map((t) => t.discount_percent));
    return Math.max(scoped, globalDiscount);
  };

  const lineUnit = (item) => {
    const d = discountFor(item);
    return d ? Math.round((item.price_cents * (100 - d)) / 100) : item.price_cents;
  };

  const discountedSubtotal = items.reduce((sum, i) => sum + lineUnit(i) * i.quantity, 0);
  const volumeDiscount = subtotal - discountedSubtotal;

  // Poids total d'envoi (les visites de ruche ne sont pas expédiées)
  const totalWeight = items.reduce((sum, i) => {
    if (i.slot_id || i.subscription) return sum;
    return sum + (weightMap[i.product_id] ?? i.shipping_weight ?? 250) * i.quantity;
  }, 0);

  // Tarif selon tranches de poids si définies, sinon prix fixe
  const priceFor = (m) => {
    const brackets = [...(m.weight_brackets || [])].sort((a, b) => a.max_weight_g - b.max_weight_g);
    if (brackets.length && totalWeight > 0) {
      const match = brackets.find((b) => totalWeight <= b.max_weight_g);
      return (match || brackets[brackets.length - 1]).price_cents;
    }
    return m.price_cents;
  };

  // Au-delà de la dernière tranche : le mode devient indisponible (devis sur mesure)
  const isOverweight = (m) => Boolean(m.max_weight_g && totalWeight > m.max_weight_g);
  const formatKg = (g) => (g / 1000).toLocaleString("fr-FR");

  const isAssoc = methodId === "association";
  const method = methods.find((m) => m.method_id === methodId) || null;
  const methodOverweight = Boolean(method && isOverweight(method));
  const shippingCost = !schoolMode && method && isAvailable(method) && !methodOverweight
    ? method.free_above_cents && discountedSubtotal >= method.free_above_cents
      ? 0
      : priceFor(method)
    : !schoolMode && isAssoc && assoc ? assoc.fee_cents : 0;
  const needsPostal = Boolean(method && method.zone && method.zone !== "all" && !postalOk);
  const methodUnavailable = Boolean(method && !isAvailable(method));
  const scheduled = Boolean(method?.scheduled);
  const isRelay = Boolean(!schoolMode && method?.carrier === "mondial_relay");
  const kidOk = Boolean(kid.first.trim() && kid.last.trim() && kid.klass.trim());
  const chequeOn = Boolean(chequeSettings?.enabled && chequeSettings?.address);
  const isCheque = chequeOn && payMethod === "cheque";
  const chequeUser = user && user.role === "customer" ? user : null;
  const chequeBlocked = isCheque && !chequeContactOk(chequeUser, chequeContact);
  const checkoutBlocked = !(user && user.role === "customer") || chequeBlocked || (schoolMode ? !kidOk : isAssoc ? !assoc : (needsPostal || methodUnavailable || methodOverweight || (scheduled && !slotId) || (isRelay && !relayPoint)));
  const gaugeMethod = schoolMode || isAssoc ? null : (method?.free_above_cents ? method : methods.find((m) => m.free_above_cents)) || null;
  const remaining = gaugeMethod ? Math.max(0, gaugeMethod.free_above_cents - discountedSubtotal) : 0;
  const progress = gaugeMethod ? Math.min(100, (discountedSubtotal / gaugeMethod.free_above_cents) * 100) : 100;

  const welcomeDiscount = Math.round(discountedSubtotal * welcome.percent / 100);
  const totalBeforeLoyalty = discountedSubtotal + shippingCost - welcomeDiscount;
  const isCustomer = Boolean(user && user.role === "customer");
  const points = isCustomer ? user.loyalty_points || 0 : 0;
  const pointValue = loyaltySettings?.point_value_cents || 10;
  const loyaltyDiscount = useLoyalty && points > 0 ? Math.min(points * pointValue, totalBeforeLoyalty) : 0;
  const pointsToUse = Math.floor(loyaltyDiscount / pointValue);
  const grandTotal = totalBeforeLoyalty - loyaltyDiscount;
  const itemsTotal = Math.max(0, discountedSubtotal - welcomeDiscount - loyaltyDiscount);
  const itemCount = items.reduce((n, i) => n + i.quantity, 0);
  useEffect(() => { if (!isOpen || items.length === 0) setStep(1); }, [isOpen, items.length]);
  const goStep = (n) => { setStep(n); document.querySelector("[data-testid=cart-scroll]")?.scrollTo(0, 0); };

  useEffect(() => {
    const r = getReferral();
    if (!isOpen || !r?.code) { setWelcome({ percent: 0, name: null }); return; }
    api.post("/referral/preview", { code: r.code, welcome: !r.welcome_used })
      .then(({ data }) => setWelcome({ percent: data.welcome_percent, name: data.referrer_name, invite: data.account_required ? { name: data.invited_by, percent: data.account_welcome_percent } : null }))
      .catch(() => setWelcome({ percent: 0, name: null }));
  }, [isOpen, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSlotId("");
    if (!scheduled || !postalOk || !method) { setSlots(null); return; }
    api.get("/delivery/slots", { params: { method_id: method.method_id, postal } }).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, [scheduled, postalOk, postal, method?.method_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, slot_id: i.slot_id || null, box_selection: i.box_selection || null, is_subscription: Boolean(i.subscription), subscription_cadence: i.subscription?.cadence || null, gift: i.gift || null })),
        origin_url: window.location.origin,
        gift_wrap: giftWrap,
        shipping_method: schoolMode ? null : methodId,
        shipping_postal: postal || null,
        association_code: !schoolMode && isAssoc && assoc ? assoc.code : null,
        delivery_slot_id: scheduled && !schoolMode ? slotId : null,
        school_pickup: schoolMode ? { student_first: kid.first.trim(), student_last: kid.last.trim(), student_class: kid.klass.trim() } : null,
        use_loyalty: useLoyalty && loyaltyDiscount > 0,
        referral_code: getReferral()?.code || null,
        referral_welcome: !getReferral()?.welcome_used,
        payment_method: isCheque ? "cheque" : "card",
        cheque_contact: isCheque && !chequeUser ? chequeContact : null,
        relay_point_id: isRelay && relayPoint ? relayPoint.id : null,
      });
      if (data.cheque) {
        setLoading(false);
        setIsOpen(false);
        navigate(`/paiement/succes?session_id=${data.session_id}`);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de lancer le paiement, réessayez.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-beeswax/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-cream shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-amber-900/10 px-6 py-5">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-bark">{step === 1 ? "Votre panier" : "Livraison & paiement"}</h2>
                {items.length > 0 && (
                  <p data-testid="cart-step-indicator" className="mt-0.5 text-xs font-medium uppercase tracking-[0.15em] text-stone-mute">
                    Étape {step} / 2 · {step === 1 ? "Vos produits" : "Livraison et paiement"}
                  </p>
                )}
              </div>
              <button
                data-testid="cart-close-button"
                onClick={() => setIsOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-800/20 text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
                aria-label="Fermer le panier"
              >
                <X size={18} />
              </button>
            </div>

            {gaugeMethod && (
              <div className="border-b border-amber-900/10 px-6 py-4">
                <div className="mb-2 flex items-center gap-2 text-sm text-bark-soft">
                  <Truck size={16} className="text-sage" />
                  {remaining > 0 ? (
                    <span data-testid="shipping-progress-label">
                      Plus que <strong className="text-amber-deep">{formatPrice(remaining)}</strong> pour la livraison offerte ({gaugeMethod.name.toLowerCase()})
                    </span>
                  ) : (
                    <span data-testid="shipping-free-label" className="font-medium text-sage">Livraison offerte, bien joué !</span>
                  )}
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-sand">
                  <div
                    data-testid="shipping-progress-bar"
                    className="h-full rounded-full bg-gradient-to-r from-honey to-amber-deep transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            <div data-testid="cart-scroll" className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {step === 2 && items.length > 0 && (
              <div className="flex items-center justify-between gap-3 border-b border-amber-900/10 px-6 py-3">
                <button type="button" data-testid="cart-back-button" onClick={() => goStep(1)} className="flex items-center gap-1.5 text-sm font-medium text-amber-deep transition-colors hover:text-honey">
                  <ArrowLeft size={15} /> Modifier mon panier
                </button>
                <span data-testid="cart-step2-summary" className="text-xs text-bark-soft">{itemCount} article{itemCount > 1 ? "s" : ""} · {formatPrice(itemsTotal)}</span>
              </div>
            )}
            <div className={`px-6 py-4 ${items.length === 0 ? "flex-1" : ""} ${step === 2 ? "hidden" : ""}`}>
              {items.length === 0 ? (
                <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <ShoppingBag size={40} className="text-amber-900/20" />
                  <p className="font-serif text-xl text-bark-soft">Votre panier attend sa première douceur…</p>
                </div>
              ) : (
                <ul className="space-y-5">
                  {items.map((item) => {
                    const lineDiscount = discountFor(item);
                    return (
                      <li key={itemKey(item)} data-testid={`cart-item-${itemKey(item)}`} className="flex gap-4">
                        <img src={item.image_url} alt={item.name} className="h-20 w-20 rounded-xl border border-amber-900/10 bg-white object-contain" />
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-serif text-base font-semibold leading-tight text-bark">{item.name}</p>
                            <button
                              data-testid={`cart-remove-${itemKey(item)}`}
                              onClick={() => removeItem(itemKey(item))}
                              className="text-stone-mute transition-colors hover:text-terra"
                              aria-label="Retirer"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <p className="text-xs text-stone-mute">{item.weight}</p>
                          {item.slot_label && (
                            <p data-testid={`cart-slot-${itemKey(item)}`} className="mt-0.5 text-xs font-medium text-amber-deep">
                              Créneau : {item.slot_label}
                            </p>
                          )}
                          {item.box_label && (
                            <p data-testid={`cart-box-${itemKey(item)}`} className="mt-0.5 text-xs font-medium text-amber-deep">
                              {item.box_label}
                            </p>
                          )}
                          {item.subscription && (
                            <p data-testid={`cart-sub-${itemKey(item)}`} className="mt-0.5 text-xs font-medium text-sage">
                              Abonnement {item.subscription.cadence === "monthly" ? "mensuel" : "bimestriel"} · {item.subscription.deliveries} livraisons/an
                            </p>
                          )}
                          {item.gift && item.gift.is_gift && (
                            <p data-testid={`cart-gift-${itemKey(item)}`} className="mt-0.5 text-xs font-medium text-terra">
                              🎁 Cadeau pour {item.gift.recipient_name}
                              {item.gift.country && item.gift.country.toLowerCase() !== "france" ? " (devis hors France)" : ""}
                            </p>
                          )}
                          {lineDiscount > 0 && (
                            <p data-testid={`cart-line-discount-${itemKey(item)}`} className="mt-0.5 text-xs font-semibold text-sage">
                              Remise volume −{lineDiscount} %
                            </p>
                          )}
                          <div className="mt-auto flex items-center justify-between pt-1">
                            <div className="flex items-center gap-2 rounded-full border border-amber-900/15 bg-white px-2 py-1">
                              <button
                                data-testid={`cart-decrease-${itemKey(item)}`}
                                onClick={() => updateQuantity(itemKey(item), item.quantity - 1)}
                                className="flex h-6 w-6 items-center justify-center rounded-full text-bark-soft hover:text-amber-deep"
                                aria-label="Diminuer"
                              >
                                <Minus size={13} />
                              </button>
                              <span data-testid={`cart-quantity-${itemKey(item)}`} className="w-5 text-center text-sm font-semibold">{item.quantity}</span>
                              <button
                                data-testid={`cart-increase-${itemKey(item)}`}
                                onClick={() => updateQuantity(itemKey(item), item.quantity + 1)}
                                className="flex h-6 w-6 items-center justify-center rounded-full text-bark-soft hover:text-amber-deep"
                                aria-label="Augmenter"
                              >
                                <Plus size={13} />
                              </button>
                            </div>
                            <span className="font-serif text-lg font-semibold text-amber-deep">
                              {lineDiscount > 0 && (
                                <span className="mr-2 align-middle text-sm font-normal text-stone-mute line-through">
                                  {formatPrice(item.price_cents * item.quantity)}
                                </span>
                              )}
                              {formatPrice(lineUnit(item) * item.quantity)}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className={`space-y-4 border-amber-900/10 px-6 py-5 ${step === 1 ? "mt-auto border-t" : ""}`}>
                {step === 2 && !(user && user.role === "customer") && <CartAccountGate />}
                {step === 2 && schoolInfo && (
                  <div data-testid="cart-delivery-mode" className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">Réception</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" data-testid="cart-mode-shipping" onClick={() => setSchoolMode(false)} className={`rounded-xl border px-3 py-2 text-left text-xs font-medium ${!schoolMode ? "border-amber-deep bg-amber-50 text-bark" : "border-amber-900/15 text-bark-soft"}`}>Livraison habituelle</button>
                      <button type="button" data-testid="cart-mode-school" onClick={() => setSchoolMode(true)} className={`rounded-xl border px-3 py-2 text-left text-xs font-medium ${schoolMode ? "border-amber-deep bg-amber-50 text-bark" : "border-amber-900/15 text-bark-soft"}`}>Remise à l'école {schoolInfo.school.name} pour mon enfant</button>
                    </div>
                    {schoolMode && (
                      <div data-testid="cart-school-child" className="space-y-2 rounded-xl border border-amber-deep/30 bg-amber-50/60 p-3">
                        <ChildPicker items={schoolInfo.children.filter((c) => !c.school_id || c.school_id === schoolInfo.school.school_id)} selectedId={kidId} onPick={(c) => { setKidId(c ? c.id : null); setKid(childToStudent(c)); }} />
                        <div className="grid grid-cols-2 gap-2">
                          <input data-testid="cart-kid-first" value={kid.first} onChange={(e) => setKid((k) => ({ ...k, first: e.target.value }))} placeholder="Prénom de l'élève *" className="rounded-lg border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber-deep" />
                          <input data-testid="cart-kid-last" value={kid.last} onChange={(e) => setKid((k) => ({ ...k, last: e.target.value }))} placeholder="Nom de l'élève *" className="rounded-lg border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber-deep" />
                          <input data-testid="cart-kid-class" value={kid.klass} onChange={(e) => setKid((k) => ({ ...k, klass: e.target.value }))} placeholder="Classe *" className="col-span-2 rounded-lg border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber-deep" />
                        </div>
                        <p className="text-xs text-bark-soft">Payé en ligne maintenant, sans frais de port : la commande est remise à votre enfant à l'école.</p>
                      </div>
                    )}
                  </div>
                )}
                {step === 2 && methods.length > 0 && !schoolMode && (
                  <div className="space-y-2" data-testid="shipping-methods">
                    <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">Mode de livraison</p>
                    <input
                      data-testid="shipping-postal-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      value={postal}
                      onChange={(e) => setPostal(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="Votre code postal (ex. 38220)"
                      className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-amber-deep"
                    />
                    {methods.map((m) => {
                      const overweight = isOverweight(m);
                      const available = isAvailable(m) && !overweight;
                      const isFree = available && Boolean(m.free_above_cents && discountedSubtotal >= m.free_above_cents);
                      const price = isFree ? 0 : priceFor(m);
                      return (
                        <label
                          key={m.method_id}
                          data-testid={`shipping-option-${m.method_id}`}
                          className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                            !available
                              ? "cursor-not-allowed border-amber-900/10 bg-sand/50 opacity-60"
                              : methodId === m.method_id
                                ? "cursor-pointer border-amber-deep bg-amber-50"
                                : "cursor-pointer border-amber-900/15 bg-white hover:border-amber-deep/50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="shipping-method"
                            checked={methodId === m.method_id}
                            onChange={() => setMethodId(m.method_id)}
                            disabled={!available}
                            className="accent-amber-deep"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-bark">{m.name}</span>
                            <span className="block truncate text-xs text-stone-mute">
                              {!isAvailable(m)
                                ? "Indisponible pour ce code postal"
                                : overweight
                                  ? `Au-delà de ${formatKg(m.max_weight_g)} kg — devis sur mesure`
                                  : m.description}
                            </span>
                          </span>
                          <span className="shrink-0 text-sm font-semibold text-amber-deep">
                            {available ? (price === 0 ? "Offerte" : formatPrice(price)) : "—"}
                          </span>
                        </label>
                      );
                    })}
                    {assocEnabled && (
                      <AssociationPicker postal={postal} postalOk={postalOk} selected={isAssoc} onSelect={() => setMethodId("association")} verified={assoc} onVerified={setAssoc} />
                    )}
                    {isRelay && postalOk && !methodUnavailable && !methodOverweight && (
                      <RelayPointPicker postal={postal} weight={totalWeight} selected={relayPoint} onSelect={setRelayPoint} />
                    )}
                    {scheduled && postalOk && !methodUnavailable && (
                      <div data-testid="delivery-slot-picker" className="rounded-xl border border-amber-deep/30 bg-amber-50/60 p-3">
                        <p className="mb-1.5 text-xs font-semibold text-bark">Choisissez votre créneau de livraison *</p>
                        {slots === null ? (
                          <p className="text-xs text-bark-soft">Chargement des créneaux…</p>
                        ) : slots.length === 0 ? (
                          <p data-testid="delivery-slots-empty" className="text-xs font-medium text-terra">Aucun créneau disponible pour le {postal} pour le moment — choisissez un autre mode ou contactez-nous.</p>
                        ) : (
                          <select data-testid="delivery-slot-select" value={slotId} onChange={(e) => setSlotId(e.target.value)} className="w-full rounded-lg border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber-deep">
                            <option value="">— Sélectionnez un créneau —</option>
                            {slots.map((sl) => <option key={sl.slot_id} value={sl.slot_id}>{sl.label}</option>)}
                          </select>
                        )}
                      </div>
                    )}
                    {needsPostal && (
                      <p data-testid="shipping-postal-hint" className="text-xs font-medium text-amber-800">
                        Renseignez votre code postal pour « {method.name} ».
                      </p>
                    )}
                    {methodUnavailable && (
                      <p data-testid="shipping-unavailable-hint" className="text-xs font-medium text-terra">
                        « {method.name} » n'est pas disponible pour le {postal} — choisissez un autre mode.
                      </p>
                    )}
                    {(methodOverweight || methods.some(isOverweight)) && (
                      <p data-testid="shipping-overweight-hint" className="text-xs font-medium text-terra">
                        {methodOverweight
                          ? `« ${method.name} » est limitée à ${formatKg(method.max_weight_g)} kg — `
                          : "Colis lourd : certaines options ne sont plus disponibles — "}
                        allégez votre panier ou{" "}
                        <Link to="/contact" data-testid="shipping-overweight-contact" onClick={() => setIsOpen(false)} className="underline underline-offset-2">
                          contactez-nous pour un devis
                        </Link>.
                      </p>
                    )}
                  </div>
                )}
                {step === 1 && (
                <label data-testid="gift-wrap-option" className="flex cursor-pointer items-center gap-3 text-sm text-bark-soft">
                  <input
                    data-testid="gift-wrap-checkbox"
                    type="checkbox"
                    checked={giftWrap}
                    onChange={(e) => setGiftWrap(e.target.checked)}
                    className="h-4 w-4 accent-amber-deep"
                  />
                  <Gift size={16} className="text-terra" />
                  Emballage cadeau (offert)
                </label>
                )}
                {step === 1 && isCustomer && points > 0 && loyaltySettings && (
                  <label
                    data-testid="loyalty-toggle"
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-amber-900/15 bg-white px-4 py-3 text-sm text-bark-soft transition-colors hover:border-amber-deep/50"
                  >
                    <input
                      data-testid="loyalty-checkbox"
                      type="checkbox"
                      checked={useLoyalty}
                      onChange={(e) => setUseLoyalty(e.target.checked)}
                      className="h-4 w-4 accent-amber-deep"
                    />
                    <Star size={16} className="fill-honey text-honey" />
                    <span className="min-w-0 flex-1">
                      Utiliser mes <strong className="text-bark">{points} points</strong> de fidélité
                      <span data-testid="loyalty-toggle-hint" className="block text-xs text-stone-mute">
                        soit jusqu'à {formatPrice(Math.min(points * pointValue, totalBeforeLoyalty))} de remise immédiate
                      </span>
                    </span>
                  </label>
                )}
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-bark-soft">Sous-total</span>
                    <span data-testid="cart-subtotal" className="font-medium text-bark">{formatPrice(subtotal)}</span>
                  </div>
                  {volumeDiscount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-bark-soft">Remise volume</span>
                      <span data-testid="cart-volume-discount" className="font-medium text-sage">−{formatPrice(volumeDiscount)}</span>
                    </div>
                  )}
                  {step === 2 && <div className="flex items-center justify-between">
                    <span className="text-bark-soft">Livraison{method && !schoolMode ? ` — ${method.name}` : !schoolMode && isAssoc && assoc ? ` — ${/^association/i.test(assoc.name) ? "" : "Association "}${assoc.name}` : ""}</span>
                    <span data-testid="cart-shipping" className="font-medium text-bark">{schoolMode ? "Remise à l'école" : isAssoc && !assoc ? "Code à valider" : shippingCost === 0 ? "Offerte" : formatPrice(shippingCost)}</span>
                  </div>}
                  {step === 1 && welcome.invite && welcome.invite.percent > 0 && (
                    <Link to="/compte/connexion?mode=inscription" onClick={() => setIsOpen(false)} data-testid="cart-welcome-signup" className="block rounded-xl bg-amber-50 px-3 py-2 text-xs text-bark transition-colors hover:bg-amber-100">
                      {welcome.invite.name} vous offre <strong>−{welcome.invite.percent} %</strong> sur votre première commande : <span className="font-semibold text-amber-deep underline">créez votre compte</span> pour en profiter.
                    </Link>
                  )}
                  {welcomeDiscount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-bark-soft">Bienvenue de la part de {welcome.name} (−{welcome.percent} %)</span>
                      <span data-testid="cart-welcome-discount" className="font-medium text-sage">−{formatPrice(welcomeDiscount)}</span>
                    </div>
                  )}
                  {loyaltyDiscount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-bark-soft">Points fidélité ({pointsToUse} pts)</span>
                      <span data-testid="cart-loyalty-discount" className="font-medium text-sage">−{formatPrice(loyaltyDiscount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-amber-900/10 pt-2">
                    <span className="font-medium text-bark">{step === 1 ? "Total hors livraison" : "Total"}</span>
                    <span data-testid={step === 1 ? "cart-items-total" : "cart-total"} className="font-serif text-2xl font-semibold text-bark">{formatPrice(step === 1 ? itemsTotal : grandTotal)}</span>
                  </div>
                </div>
                {step === 2 && chequeOn && (
                  <PaymentMethodPicker settings={chequeSettings} method={payMethod} setMethod={setPayMethod} user={chequeUser} contact={chequeContact} setContact={setChequeContact} onNavigate={() => setIsOpen(false)} />
                )}
                <div className="sticky bottom-0 z-10 -mx-6 -mb-5 bg-cream px-6 pb-5 pt-3 shadow-[0_-8px_16px_-12px_rgba(60,40,10,0.25)]">
                {step === 1 ? (
                <button
                  data-testid="cart-next-button"
                  onClick={() => goStep(2)}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-4 text-base font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98]"
                >
                  Continuer : livraison et paiement <ArrowRight size={18} />
                </button>
                ) : (
                <button
                  data-testid="checkout-button"
                  onClick={handleCheckout}
                  disabled={loading || checkoutBlocked}
                  className="w-full rounded-full bg-amber-deep py-4 text-base font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (isCheque ? "Validation…" : "Redirection vers le paiement…") : isCheque ? "Valider ma commande (chèque)" : "Commander"}
                </button>
                )}
                </div>
              </div>
            )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
